/**
 * AuthContext and AuthProvider
 *
 * This module implements the authentication context for the application.
 *
 * TODO: Implementing token refresh mechanism.
 * TODO: Add error handling for JSON parsing in useEffect.
 * TODO: Implement secure storage for tokens (e.g., encrypted localStorage or httpOnly cookies).
 */

import { getAuthTokens, saveAuthTokens, TOKEN_STORAGE_KEY } from "@/taskpane/utils/client";
import { Tokens } from "@/taskpane/types/auth";
import { isValidCognitoToken, decodeCognitoToken, getTokenExpirationTime } from "@/taskpane/utils/jwt";
import { getUserAttributes } from "@/taskpane/services/auth";
import React, { createContext, useEffect, useState } from "react";

interface Office {
  Nom: string;
  NumeroCRPCEN: string;
  TenantId: string;
}

interface AuthContextProps {
  isAuthenticated: boolean;
  setTokens: (newTokens: Omit<Tokens, "ExpiresOn">) => void;
  logout: () => void;
  tokens?: Tokens;
  isLoading: boolean;
  isProcessingUrlToken: boolean;
}

export const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  setTokens() {},
  logout() {},
  isLoading: true,
  isProcessingUrlToken: false,
});

// TODO: Implement secure storage for tokens (e.g., encrypted localStorage or httpOnly cookies).

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Check if there's any token in URL on initial load
  const initialUrlHasToken = (): boolean => {
    const hash = window.location.hash;
    const search = window.location.search;
    const href = window.location.href;

    // Check for both old and new token formats
    const hasToken = !!(
      (hash && (hash.includes('?token=') || hash.includes('?idToken=') || hash.includes('&idToken='))) || 
      (search && (search.includes('token=') || search.includes('idToken='))) || 
      (href && (href.includes('token=') || href.includes('idToken=')))
    );

    // console.log('[AuthContext] initialUrlHasToken check - href:', href);
    // console.log('[AuthContext] initialUrlHasToken check - hash:', hash, 'search:', search, 'hasToken:', hasToken);
    return hasToken;
  };

  // Initialize states with proper values
  const hasInitialUrlToken = initialUrlHasToken();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [tokens, setTokens] = useState<Tokens | undefined>(undefined);
  const [hasCheckedUrlToken, setHasCheckedUrlToken] = useState(false);
  const [isProcessingUrlToken, setIsProcessingUrlToken] = useState(hasInitialUrlToken);

  // console.log('[AuthContext] Initial state - hasInitialUrlToken:', hasInitialUrlToken, 'isProcessingUrlToken:', hasInitialUrlToken);

  const setTokensFct = (newTokens: Omit<Tokens, "ExpiresOn">) => {
    // console.log('[AuthContext] setTokensFct called with tokens');
    const savedTokens = saveAuthTokens(newTokens);
    // console.log('[AuthContext] Tokens saved, setting authenticated to true');
    setIsAuthenticated(true);
    setTokens(savedTokens);
    // console.log('[AuthContext] Authentication state and tokens updated');
  };

  const logout = () => {
    setIsAuthenticated(false);
    setTokens(undefined);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('session');
    
    // Clear SSO-related storage items
    localStorage.removeItem('sso_session_id');
    localStorage.removeItem('sso_state');
    localStorage.removeItem('sso_initiated_at');
    sessionStorage.removeItem('sso_state');
    sessionStorage.removeItem('sso_code_verifier');
    sessionStorage.removeItem('sso_redirect_uri');
    sessionStorage.removeItem('sso_reset');
  };

  useEffect(() => {
    (async () => {
      try {
        // console.log('[AuthContext] Starting authentication check...');
        // console.log('[AuthContext] Current URL:', window.location.href);
        // console.log('[AuthContext] Current hash:', window.location.hash);
        // console.log('[AuthContext] Current search:', window.location.search);
        // console.log('[AuthContext] isProcessingUrlToken at start of useEffect:', isProcessingUrlToken);

        // Check for token in URL parameters first (handle hash-based routing)
        let tokenFromUrl = null;

        // Check both hash and search parameters for the token
        const hash = window.location.hash;
        const search = window.location.search;

        // Extract all tokens from URL
        let idTokenFromUrl = null;
        let accessTokenFromUrl = null;
        let refreshTokenFromUrl = null;
        let expiresInFromUrl = null;

        // Check for query params in hash after '?'
        if (hash && hash.includes('?')) {
          // console.log('[AuthContext] Params found in hash, extracting...');
          const hashQueryString = hash.substring(hash.indexOf('?') + 1);
          const hashParams = new URLSearchParams(hashQueryString);

          // Try new format first
          idTokenFromUrl = hashParams.get('idToken');
          accessTokenFromUrl = hashParams.get('accessToken');
          refreshTokenFromUrl = hashParams.get('refreshToken');
          expiresInFromUrl = hashParams.get('expiresIn');

          // Fallback to legacy format
          if (!idTokenFromUrl) {
            idTokenFromUrl = hashParams.get('token');
          }

          // console.log('[AuthContext] Extracted tokens from hash:', {
          //   idToken: idTokenFromUrl ? 'PRESENT' : 'MISSING',
          //   accessToken: accessTokenFromUrl ? 'PRESENT' : 'MISSING',
          //   refreshToken: refreshTokenFromUrl ? 'PRESENT' : 'MISSING',
          //   expiresIn: expiresInFromUrl || 'MISSING'
          // });
        } else if (search) {
          // console.log('[AuthContext] Checking search params for tokens...');
          const urlParams = new URLSearchParams(search);

          // Try new format first
          idTokenFromUrl = urlParams.get('idToken');
          accessTokenFromUrl = urlParams.get('accessToken');
          refreshTokenFromUrl = urlParams.get('refreshToken');
          expiresInFromUrl = urlParams.get('expiresIn');

          // Fallback to legacy format
          if (!idTokenFromUrl) {
            idTokenFromUrl = urlParams.get('token');
          }

          // console.log('[AuthContext] Extracted tokens from search:', {
          //   idToken: idTokenFromUrl ? 'PRESENT' : 'MISSING',
          //   accessToken: accessTokenFromUrl ? 'PRESENT' : 'MISSING',
          //   refreshToken: refreshTokenFromUrl ? 'PRESENT' : 'MISSING',
          //   expiresIn: expiresInFromUrl || 'MISSING'
          // });
        } else {
          // console.log('[AuthContext] No tokens found in URL parameters');
        }

        // For backward compatibility, use idToken as the main token
        tokenFromUrl = idTokenFromUrl;

        // If we found a token, set isProcessingUrlToken to true
        if (tokenFromUrl && !hasCheckedUrlToken) {
          // console.log('[AuthContext] Token found in URL, setting isProcessingUrlToken to true');
          setIsProcessingUrlToken(true);
        }

        // Processing state is already set during initialization if token exists

        // Log state before processing
        // console.log('[AuthContext] hasCheckedUrlToken state before processing:', hasCheckedUrlToken);
        // console.log('[AuthContext] isAuthenticated state before processing:', isAuthenticated);
        // console.log('[AuthContext] isLoading state before processing:', isLoading);
        // console.log('[AuthContext] tokens state before processing:', tokens);
        // console.log('[AuthContext] tokenFromUrl:', tokenFromUrl);
        // console.log('[AuthContext] isProcessingUrlToken state before processing:', isProcessingUrlToken);

        setHasCheckedUrlToken(true);


        if (tokenFromUrl) {
          // console.log('[AuthContext] Processing token from URL...');

          // Validate the Cognito token
          if (!isValidCognitoToken(tokenFromUrl)) {
            console.error('[AuthContext] Invalid Cognito token found in URL');
            setIsProcessingUrlToken(false);
            setLoading(false);
            return;
          }

          // console.log('[AuthContext] Valid Cognito token found, decoding...');
          const decodedToken = decodeCognitoToken(tokenFromUrl);

          if (!decodedToken) {
            console.error('[AuthContext] Failed to decode Cognito token');
            setIsProcessingUrlToken(false);
            setLoading(false);
            return;
          }

          // console.log('[AuthContext] Token decoded successfully:', {
          //   email: decodedToken.email,
          //   tokenUse: decodedToken.token_use,
          //   expiresAt: new Date(decodedToken.exp * 1000).toISOString(),
          //   issuer: decodedToken.iss
          // });

          // Calculate expiration time
          let expiresIn = 3600; // Default 1 hour
          if (expiresInFromUrl) {
            expiresIn = parseInt(expiresInFromUrl, 10);
            // console.log('[AuthContext] Using expiresIn from URL:', expiresIn);
          } else {
            const expirationTime = getTokenExpirationTime(tokenFromUrl);
            const currentTime = Math.floor(Date.now() / 1000);
            expiresIn = expirationTime ? expirationTime - currentTime : 3600;
            // console.log('[AuthContext] Calculated expiresIn from token:', expiresIn);
          }

          // Create token object with all available tokens
          const tempTokens = {
            IdToken: idTokenFromUrl || '',
            AccessToken: accessTokenFromUrl || '',
            RefreshToken: refreshTokenFromUrl || '',
            TokenType: 'Bearer',
            ExpiresIn: Math.max(expiresIn, 0) // Ensure non-negative value
          };

          // console.log('[AuthContext] Created token object with:', {
          //   hasIdToken: !!tempTokens.IdToken,
          //   hasAccessToken: !!tempTokens.AccessToken,
          //   hasRefreshToken: !!tempTokens.RefreshToken,
          //   expiresIn: tempTokens.ExpiresIn
          // });

          // console.log('[AuthContext] Saving validated tokens and setting authenticated state...');
          setTokensFct(tempTokens);
          // console.log('[AuthContext] Authentication state should now be true');

          // Check if URL contains ?tenant_id parameter for automatic office switch (SAME AS CRPCEN)
          // Priority: window.location.search (before hash) > hash query params (after hash)
          let urlTenantId = new URLSearchParams(window.location.search).get('tenantId');
          if (!urlTenantId && hash && hash.includes('?')) {
            urlTenantId = new URLSearchParams(hash.substring(hash.indexOf('?') + 1)).get('tenantId');
          }
          
          if (urlTenantId) {
            try {
              // Get current user's TenantId
              const currentTenantId = localStorage.getItem('selected_office_tenant_id');
              const currentCrpcen = decodedToken['custom:CRPCEN'];
              
              // Load available offices when available so we can compare tenant and actual token CRPCEN.
              const officesStr = localStorage.getItem('available_offices');
              const offices: Office[] = officesStr ? JSON.parse(officesStr) : [];
              const targetOffice = offices.find(office => office.TenantId === urlTenantId);
              const shouldSwitchTenant =
                currentTenantId !== urlTenantId ||
                (!!targetOffice?.NumeroCRPCEN && currentCrpcen !== targetOffice.NumeroCRPCEN);

              // Only switch if the tenant differs, or if the stored tenant matches but the token CRPCEN does not.
              if (shouldSwitchTenant) {
                if (targetOffice) {
                    // Store target office info (BEFORE logout - logout doesn't touch these keys)
                    localStorage.setItem('target_office_tenant_id', targetOffice.TenantId);
                    localStorage.setItem('target_office_name', targetOffice.Nom);
                    localStorage.setItem('target_office_crpcen', targetOffice.NumeroCRPCEN || '');
                    
                    // Store the intended destination URL to redirect after login
                    const destinationPath = window.location.hash;
                    localStorage.setItem('redirect_after_tenant_switch', destinationPath);
                    
                    // Logout to clear tokens
                    logout();
                    
                    // Force reload to login page - login will detect target_office_tenant_id and auto-trigger SSO
                    window.location.href = `${window.location.origin}${window.location.pathname}#/login`;
                    window.location.reload();
                    return;
                } else {
                  console.warn('[AuthContext] TenantId', urlTenantId, 'not found in available offices');
                }

                // Deep links can arrive in a fresh browser context where the GenAPI
                // office list is not cached yet. Preserve the tenant intent and let
                // the SSO callback/backend resolve it.
                localStorage.setItem('target_office_tenant_id', urlTenantId);
                localStorage.removeItem('target_office_crpcen');
                localStorage.setItem('redirect_after_tenant_switch', window.location.hash);

                logout();
                window.location.href = `${window.location.origin}${window.location.pathname}#/login`;
                window.location.reload();
                return;
              }
            } catch (error) {
              console.error('[AuthContext] Error checking TenantId for auto-switch:', error);
            }
          }

          // Check if URL contains ?crpcen parameter for automatic office switch
          // Priority: window.location.search (before hash) > hash query params (after hash)
          let urlCrpcen = new URLSearchParams(window.location.search).get('crpcen');
          if (!urlCrpcen && hash && hash.includes('?')) {
            urlCrpcen = new URLSearchParams(hash.substring(hash.indexOf('?') + 1)).get('crpcen');
          }
          
          if (urlCrpcen) {
            try {
              // Get current user's CRPCEN
              const currentUserData = await getUserAttributes();
              const currentCrpcen = currentUserData?.crpcen;
              
              // Only switch if different
              if (currentCrpcen !== urlCrpcen) {
                // Load available offices
                const officesStr = localStorage.getItem('available_offices');
                
                if (officesStr) {
                  const offices: Office[] = JSON.parse(officesStr);
                  const targetOffice = offices.find(office => office.NumeroCRPCEN === urlCrpcen);
                  
                  if (targetOffice) {
                    // Store target office info (BEFORE logout - logout doesn't touch these keys)
                    localStorage.setItem('target_office_tenant_id', targetOffice.TenantId);
                    localStorage.setItem('target_office_name', targetOffice.Nom);
                    localStorage.setItem('target_office_crpcen', targetOffice.NumeroCRPCEN);
                    
                    // Store the intended destination URL to redirect after login
                    const destinationPath = window.location.hash;
                    localStorage.setItem('redirect_after_crpcen_switch', destinationPath);
                    
                    // Logout to clear tokens
                    logout();
                    
                    // Force reload to login page - login will detect target_office_crpcen and auto-trigger SSO
                    window.location.href = `${window.location.origin}${window.location.pathname}#/login`;
                    window.location.reload();
                    return;
                  } else {
                    console.warn('[AuthContext] CRPCEN', urlCrpcen, 'not found in available offices');
                  }
                } else {
                  console.warn('[AuthContext] No available offices in localStorage');
                }
              }
            } catch (error) {
              console.error('[AuthContext] Error checking CRPCEN for auto-switch:', error);
            }
          }

          // Clean the URL by removing all token parameters, crpcen and tenant_id
          if (hash && hash.includes('?')) {
            const cleanHash = hash.split('?')[0];
            // console.log('[AuthContext] Cleaning hash URL from:', hash, 'to:', cleanHash);
            window.location.hash = cleanHash;
          } else if (search) {
            const newUrl = new URL(window.location.href);
            // Remove all token-related parameters
            newUrl.searchParams.delete('token');
            newUrl.searchParams.delete('idToken');
            newUrl.searchParams.delete('accessToken');
            newUrl.searchParams.delete('refreshToken');
            newUrl.searchParams.delete('expiresIn');
            newUrl.searchParams.delete('crpcen'); // Also remove crpcen parameter
            newUrl.searchParams.delete('tenantId'); // Also remove tenant_id parameter
            // console.log('[AuthContext] Cleaning search URL to:', newUrl.toString());
            window.history.replaceState({}, document.title, newUrl.toString());
          }

          // Set loading to false after processing token
          // Use setTimeout to ensure state updates have propagated
          // console.log('[AuthContext] Token processing complete, will set loading states to false');
          setTimeout(() => {
            // console.log('[AuthContext] Setting loading states to false after token processing');
            setIsProcessingUrlToken(false);
            setLoading(false);
          }, 100);
        } else {
          // Check for stored tokens
          const storedTokens = await getAuthTokens();
          if (storedTokens) {
            setIsAuthenticated(true);
            setTokens(storedTokens);
            
            // Check if URL contains ?tenant_id parameter for automatic office switch (for already logged-in users)
            // Priority: window.location.search (before hash) > hash query params (after hash)
            let urlTenantId = new URLSearchParams(window.location.search).get('tenantId');
            if (!urlTenantId && hash && hash.includes('?')) {
              urlTenantId = new URLSearchParams(hash.substring(hash.indexOf('?') + 1)).get('tenantId');
            }
            
            if (urlTenantId) {
              try {
                // Get current user's TenantId
                const currentTenantId = localStorage.getItem('selected_office_tenant_id');
                const currentCrpcen = decodeCognitoToken(storedTokens.IdToken)?.['custom:CRPCEN'];
                
                // Load available offices when available so we can compare tenant and actual token CRPCEN.
                const officesStr = localStorage.getItem('available_offices');
                const offices: Office[] = officesStr ? JSON.parse(officesStr) : [];
                const targetOffice = offices.find(office => office.TenantId === urlTenantId);
                const shouldSwitchTenant =
                  currentTenantId !== urlTenantId ||
                  (!!targetOffice?.NumeroCRPCEN && currentCrpcen !== targetOffice.NumeroCRPCEN);

                // Only switch if the tenant differs, or if the stored tenant matches but the token CRPCEN does not.
                if (shouldSwitchTenant) {
                  if (targetOffice) {
                      // Store target office info (BEFORE logout - logout doesn't touch these keys)
                      localStorage.setItem('target_office_tenant_id', targetOffice.TenantId);
                      localStorage.setItem('target_office_name', targetOffice.Nom);
                      localStorage.setItem('target_office_crpcen', targetOffice.NumeroCRPCEN || '');
                      
                      // Store the intended destination URL to redirect after login
                      const destinationPath = window.location.hash;
                      localStorage.setItem('redirect_after_tenant_switch', destinationPath);
                      
                      // Logout to clear tokens
                      logout();
                      
                      // Force reload to login page - login will detect target_office_tenant_id and auto-trigger SSO
                      window.location.href = `${window.location.origin}${window.location.pathname}#/login`;
                      window.location.reload();
                      return;
                  } else {
                    console.warn('[AuthContext] TenantId', urlTenantId, 'not found in available offices');
                  }

                  // Deep links can arrive in a fresh browser context where the GenAPI
                  // office list is not cached yet. Preserve the tenant intent and let
                  // the SSO callback/backend resolve it.
                  localStorage.setItem('target_office_tenant_id', urlTenantId);
                  localStorage.removeItem('target_office_crpcen');
                  localStorage.setItem('redirect_after_tenant_switch', window.location.hash);

                  logout();
                  window.location.href = `${window.location.origin}${window.location.pathname}#/login`;
                  window.location.reload();
                  return;
                }
              } catch (error) {
                console.error('[AuthContext] Error checking TenantId for auto-switch:', error);
              }
            }
            
            // Check if URL contains ?crpcen parameter for automatic office switch (for already logged-in users)
            // Priority: window.location.search (before hash) > hash query params (after hash)
            let urlCrpcen = new URLSearchParams(window.location.search).get('crpcen');
            if (!urlCrpcen && hash && hash.includes('?')) {
              urlCrpcen = new URLSearchParams(hash.substring(hash.indexOf('?') + 1)).get('crpcen');
            }
            
            if (urlCrpcen) {
              try {
                // Get current user's CRPCEN
                const currentUserData = await getUserAttributes();
                const currentCrpcen = currentUserData?.crpcen;
                
                // Only switch if different
                if (currentCrpcen !== urlCrpcen) {
                  // Load available offices
                  const officesStr = localStorage.getItem('available_offices');
                  
                  if (officesStr) {
                    const offices: Office[] = JSON.parse(officesStr);
                    const targetOffice = offices.find(office => office.NumeroCRPCEN === urlCrpcen);
                    
                    if (targetOffice) {
                      // Store target office info (BEFORE logout - logout doesn't touch these keys)
                      localStorage.setItem('target_office_tenant_id', targetOffice.TenantId);
                      localStorage.setItem('target_office_name', targetOffice.Nom);
                      localStorage.setItem('target_office_crpcen', targetOffice.NumeroCRPCEN);
                      
                      // Store the intended destination URL to redirect after login
                      const destinationPath = window.location.hash;
                      localStorage.setItem('redirect_after_crpcen_switch', destinationPath);
                      
                      // Logout to clear tokens
                      logout();
                      
                      // Force reload to login page - login will detect target_office_crpcen and auto-trigger SSO
                      window.location.href = `${window.location.origin}${window.location.pathname}#/login`;
                      window.location.reload();
                      return;
                    } else {
                      console.warn('[AuthContext] CRPCEN', urlCrpcen, 'not found in available offices');
                    }
                  } else {
                    console.warn('[AuthContext] No available offices in localStorage');
                  }
                }
              } catch (error) {
                console.error('[AuthContext] Error checking CRPCEN for auto-switch:', error);
              }
            }
          }

          // Set loading to false after checking stored tokens
          setIsProcessingUrlToken(false);
          setLoading(false);
        }
        // console.log('[AuthContext] Authentication check completed');
      } catch (error) {
        console.error("[AuthContext] Error parsing authentication tokens:", error);
        logout();
        setIsProcessingUrlToken(false);
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, setTokens: setTokensFct, logout, tokens, isLoading, isProcessingUrlToken }}>
      {children}
    </AuthContext.Provider>
  );
};
